import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { isHotkeyPressed } from 'react-hotkeys-hook';
import { chakra } from '@chakra-ui/react';
import {
  LineSegment2,
  Point2,
} from '../../../../domain/geometry/geometric-types';
import { useSheetViewer } from '../../../common/SheetViewer';
import { getUnitScaleFromCalibration } from '../../../../domain/sheet-calibration';
import { useUserTenant } from '../../../../services/auth-info';
import { DraggableVertex } from '../SheetVertex';
import {
  InitialShapeDrawingState,
  ShapeDrawingMode,
  WedgesDrawingResult,
} from '../SheetShapeDrawing';
import { DiamondLabel } from './DiamondLabel';
import {
  Diamond,
  KileTypeSKU,
  DragStateType,
  WedgeCalculationResult,
} from './wedge-types';
import { calculateWedgePoints, selectWedgeType } from './wedge-calculations';
import { findClosestParallelEdgeToSegment } from './edge-detection';
import {
  handleStartPointDrag,
  handleEndPointDrag,
  handleMiddlePointDrag,
} from './wedge-drag-handlers';
import { WedgePreviewLabel } from './WedgePreviewLabel';
import { HEADER_HEIGHT } from 'src/theme';

// Split the drag handling into multiple simpler functions
const handleStartDragCase = (
  newPoint: Point2,
  isPointerUp: boolean,
  points: Point2[],
  recalculateWedgePoints: (
    startPoint: Point2,
    endPoint: Point2
  ) => WedgeCalculationResult | null,
  setPoints: React.Dispatch<React.SetStateAction<Point2[]>>,
  emitResult: (
    points: Point2[],
    diamonds: Diamond[],
    kileType: KileTypeSKU[],
    totalWedgeProductLength: number
  ) => void,
  endDragOperation: () => void,
  currentDiamonds: Diamond[],
  currentKileTypes: KileTypeSKU[]
) => {
  const result = handleStartPointDrag(newPoint, points, recalculateWedgePoints);
  if (result) {
    const { wedgePoints } = result;
    setPoints(wedgePoints);

    if (isPointerUp) {
      emitResult(wedgePoints, currentDiamonds, currentKileTypes, 0);
      endDragOperation();
    }
  }
};

const handleEndDragCase = (
  newPoint: Point2,
  isPointerUp: boolean,
  points: Point2[],
  recalculateWedgePoints: (
    startPoint: Point2,
    endPoint: Point2
  ) => WedgeCalculationResult | null,
  setPoints: React.Dispatch<React.SetStateAction<Point2[]>>,
  emitResult: (
    points: Point2[],
    diamonds: Diamond[],
    kileType: KileTypeSKU[],
    totalWedgeProductLength: number
  ) => void,
  endDragOperation: () => void,
  currentDiamonds: Diamond[],
  currentKileTypes: KileTypeSKU[]
) => {
  const result = handleEndPointDrag(newPoint, points, recalculateWedgePoints);
  if (result) {
    const { wedgePoints } = result;
    setPoints(wedgePoints);

    if (isPointerUp) {
      emitResult(wedgePoints, currentDiamonds, currentKileTypes, 0);
      endDragOperation();
    }
  }
};

const handleMiddleDragCase = (
  newPoint: Point2,
  isPointerUp: boolean,
  dragIndexRef: React.MutableRefObject<number | null>,
  points: Point2[],
  diamonds: Diamond[],
  kileTypes: KileTypeSKU[],
  unitScale: number,
  setPoints: React.Dispatch<React.SetStateAction<Point2[]>>,
  emitResult: (
    points: Point2[],
    diamonds: Diamond[],
    kileType: KileTypeSKU[],
    totalWedgeProductLength: number
  ) => void,
  endDragOperation: () => void
) => {
  if (dragIndexRef.current !== null) {
    const result = handleMiddlePointDrag(
      newPoint,
      dragIndexRef.current,
      points,
      diamonds,
      kileTypes,
      unitScale
    );
    if (result) {
      const { newPoints } = result;

      setPoints(newPoints);

      if (isPointerUp) {
        emitResult(newPoints, diamonds, kileTypes, 0);
        endDragOperation();
      }
    }
  } else {
    console.warn('Middle point drag called but dragIndexRef.current is null');
  }
};

// Simplified function to process drag by type
const processDragByType = (
  dragState: DragStateType,
  newPoint: Point2,
  isPointerUp: boolean,
  dragIndexRef: React.MutableRefObject<number | null>,
  points: Point2[],
  diamonds: Diamond[],
  kileTypes: KileTypeSKU[],
  unitScale: number,
  recalculateWedgePoints: (
    startPoint: Point2,
    endPoint: Point2
  ) => WedgeCalculationResult | null,
  setPoints: React.Dispatch<React.SetStateAction<Point2[]>>,
  emitResult: (
    points: Point2[],
    diamonds: Diamond[],
    kileType: KileTypeSKU[],
    totalWedgeProductLength: number
  ) => void,
  endDragOperation: () => void
) => {
  switch (dragState) {
    case 'start':
      handleStartDragCase(
        newPoint,
        isPointerUp,
        points,
        recalculateWedgePoints,
        setPoints,
        emitResult,
        endDragOperation,
        diamonds,
        kileTypes
      );
      break;
    case 'end':
      handleEndDragCase(
        newPoint,
        isPointerUp,
        points,
        recalculateWedgePoints,
        setPoints,
        emitResult,
        endDragOperation,
        diamonds,
        kileTypes
      );
      break;
    case 'middle':
      handleMiddleDragCase(
        newPoint,
        isPointerUp,
        dragIndexRef,
        points,
        diamonds,
        kileTypes,
        unitScale,
        setPoints,
        emitResult,
        endDragOperation
      );
      break;
    case 'none':
      // If we somehow get here with 'none' state, use the index to determine what to do
      if (dragIndexRef.current === 0) {
        handleStartDragCase(
          newPoint,
          isPointerUp,
          points,
          recalculateWedgePoints,
          setPoints,
          emitResult,
          endDragOperation,
          diamonds,
          kileTypes
        );
      } else if (dragIndexRef.current === points.length - 1) {
        handleEndDragCase(
          newPoint,
          isPointerUp,
          points,
          recalculateWedgePoints,
          setPoints,
          emitResult,
          endDragOperation,
          diamonds,
          kileTypes
        );
      } else if (dragIndexRef.current !== null) {
        handleMiddleDragCase(
          newPoint,
          isPointerUp,
          dragIndexRef,
          points,
          diamonds,
          kileTypes,
          unitScale,
          setPoints,
          emitResult,
          endDragOperation
        );
      }
      break;
  }
};

// eslint-disable-next-line complexity
export const SheetWedgeShapeDrawingV2 = ({
  initialState,
  onResult,
  getPointInPdfCoordinateSystem,
  getPointInDomCoordinateSystem,
}: {
  initialState?: InitialShapeDrawingState[ShapeDrawingMode.Wedges];
  onResult: (result: WedgesDrawingResult) => void;
  getPointInPdfCoordinateSystem: (pointInPdf: Point2) => Point2;
  getPointInDomCoordinateSystem: (pointInPdf: Point2) => Point2;
}) => {
  const [points, setPoints] = useState<Point2[]>(initialState?.points ?? []);
  const [diamonds, setDiamonds] = useState<Diamond[]>([
    {
      triangle1: true,
      triangle2: true,
      triangle3: true,
      triangle4: true,
    },
  ]);
  const [kileTypes, setKileTypes] = useState<KileTypeSKU[]>([
    KileTypeSKU.RW257379,
  ]);

  // State to store closest edges for each segment
  const [closestEdges, setClosestEdges] = useState<LineSegment2[]>([]);
  // State to store closest edges during preview
  const [previewClosestEdges, setPreviewClosestEdges] = useState<
    LineSegment2[]
  >([]);

  // State to track the current default middle wedge type for preview
  const [defaultMiddleWedgeType, setDefaultMiddleWedgeType] =
    useState<KileTypeSKU>(KileTypeSKU.RW257379);

  const { isProtan } = useUserTenant();
  const [intermediatePoint, setIntermediatePoint] = useState<Point2 | null>(
    null
  );
  const {
    calibration: { calibration },
    shapesManager: { renderedSheetShapes },
  } = useSheetViewer();
  const unitScale = useMemo(
    () =>
      calibration ? getUnitScaleFromCalibration(calibration.calibration) : null,
    [calibration]
  );

  const isShiftPressed = isHotkeyPressed('shift');

  // Get a sorted list of available wedge types by length
  const availableWedgeTypes = useMemo(() => {
    const types: { type: KileTypeSKU; length: number }[] = [];

    // Extract kile types and their lengths from the calculations utility
    Object.values(KileTypeSKU).forEach((key) => {
      if (key !== KileTypeSKU.INVALID_KILE) {
        const length = selectWedgeType(
          {
            triangle1: true,
            triangle2: true,
            triangle3: true,
            triangle4: true,
          },
          2.4
        );
        types.push({
          type: key,
          length: 2.4, // Default for the sorted list - actual lengths will be calculated on demand
        });
      }
    });

    // Sort by length (ascending)
    return types.sort((a, b) => a.length - b.length);
  }, []);

  // Save previous visualization to avoid unnecessary recalculations
  const prevVisualizationRef = useRef<{
    points: Point2[];
    kileTypes: KileTypeSKU[];
    diamonds: Diamond[];
    mousePosition?: Point2;
  } | null>(null);

  // Track if edge detection has updated the triangles in the current visualization
  const edgeDetectionProcessedRef = useRef(false);

  // Use this to reduce flickering - only update diamonds state after a small delay
  const debounceTimerRef = useRef<number | null>(null);
  const lastUpdatedDiamondsRef = useRef<Diamond[] | null>(null);

  // Helper function to update diamonds with debounce to reduce flickering
  const updateDiamondsWithDebounce = useCallback((newDiamonds: Diamond[]) => {
    // If we have an ongoing timer, clear it
    if (debounceTimerRef.current !== null) {
      window.clearTimeout(debounceTimerRef.current);
    }

    // Store the latest diamonds
    lastUpdatedDiamondsRef.current = newDiamonds;

    // Set a new timer
    debounceTimerRef.current = window.setTimeout(() => {
      if (lastUpdatedDiamondsRef.current) {
        setDiamonds([...lastUpdatedDiamondsRef.current]);
        lastUpdatedDiamondsRef.current = null;
      }
      debounceTimerRef.current = null;
    }, 100); // 100ms debounce
  }, []);

  // Helper function to check if we need to recalculate the wedge visualization
  const needsWedgeRecalculation = useCallback(
    (currentPoint: Point2): boolean => {
      if (
        !prevVisualizationRef.current ||
        !prevVisualizationRef.current.mousePosition
      ) {
        return true;
      }

      const prevPoint = prevVisualizationRef.current.mousePosition;
      return (
        Math.abs(prevPoint[0] - currentPoint[0]) > 1 ||
        Math.abs(prevPoint[1] - currentPoint[1]) > 1
      );
    },
    []
  );

  // Modified calculation function to use the current defaultMiddleWedgeType
  const recalculateWedgePoints = useCallback(
    (startPoint: Point2, endPoint: Point2) => {
      if (!unitScale) return null;

      return calculateWedgePoints(
        startPoint,
        endPoint,
        unitScale,
        diamonds[0],
        defaultMiddleWedgeType
      );
    },
    [unitScale, diamonds, defaultMiddleWedgeType]
  );

  // Calculated intermediate wedge points when hovering - memoized for performance
  const wedgeVisualization = useMemo(() => {
    if (!unitScale || !intermediatePoint || points.length === 0) {
      edgeDetectionProcessedRef.current = false;
      return null;
    }

    // Check if significant mouse movement has occurred
    const shouldRecalculate = needsWedgeRecalculation(intermediatePoint);

    // Reset edge detection processed flag when recalculating
    if (shouldRecalculate) {
      edgeDetectionProcessedRef.current = false;
    }

    // Return cached value if no significant changes and edge detection was processed
    if (
      prevVisualizationRef.current &&
      !shouldRecalculate &&
      edgeDetectionProcessedRef.current
    ) {
      return prevVisualizationRef.current;
    }

    const startPoint = points[0];
    // Calculate new wedge points
    const result = calculateWedgePoints(
      startPoint,
      intermediatePoint,
      unitScale,
      diamonds[0],
      defaultMiddleWedgeType
    );

    // Store the mouse position to detect significant movement
    result.mousePosition = intermediatePoint;

    // Cache the result
    prevVisualizationRef.current = result;
    return result;
  }, [
    points,
    intermediatePoint,
    unitScale,
    diamonds,
    defaultMiddleWedgeType,
    needsWedgeRecalculation,
  ]);

  // Effect for finding closest edges during preview
  useEffect(() => {
    if (wedgeVisualization && renderedSheetShapes && unitScale) {
      // Skip if already processed this visualization
      if (edgeDetectionProcessedRef.current) {
        return;
      }

      const segments: LineSegment2[] = [];
      // Create segments from wedge visualization points
      for (let i = 0; i < wedgeVisualization.points.length - 1; i++) {
        segments.push([
          wedgeVisualization.points[i],
          wedgeVisualization.points[i + 1],
        ]);
      }

      // Find closest edge for each segment
      const newClosestEdges: LineSegment2[] = [];
      // Create new preview diamonds based on edge detection - starting with all triangles visible
      const newDiamonds: Diamond[] = wedgeVisualization.diamonds.map(
        (d, diamondIndex) => {
          // Apply special rules for first and last segments
          if (diamondIndex === 0) {
            // First segment should never have triangle1 or triangle3 visible
            return {
              triangle1: false,
              triangle2: true,
              triangle3: false,
              triangle4: true,
            };
          } else if (diamondIndex === wedgeVisualization.diamonds.length - 1) {
            // Last segment should never have triangle2 or triangle4 visible
            return {
              triangle1: true,
              triangle2: false,
              triangle3: true,
              triangle4: false,
            };
          } else {
            // Middle segments start with all triangles visible
            return {
              triangle1: true,
              triangle2: true,
              triangle3: true,
              triangle4: true,
            };
          }
        }
      );

      let hasChanges = false;

      segments.forEach((segment, index) => {
        const closestEdgeResult = findClosestParallelEdgeToSegment(
          segment,
          renderedSheetShapes,
          0.1,
          unitScale
        );

        if (closestEdgeResult) {
          newClosestEdges.push(closestEdgeResult.edge);

          // Check if edge is closer than 1m and adjust diamonds accordingly
          if (
            closestEdgeResult.isCloserThan100mm &&
            index < newDiamonds.length
          ) {
            hasChanges = true;
            // Skip the special triangles for first and last segments
            if (index === 0) {
              // First segment: only modify triangle2 and triangle4 based on edge position
              if (closestEdgeResult.isOnLeftSide) {
                // Do nothing to triangle1 (already false)
                newDiamonds[index].triangle2 = false;
                // Do nothing to triangle3 (already false)
                // newDiamonds[index].triangle4 remains true
              } else {
                // Do nothing to triangle1 (already false)
                // newDiamonds[index].triangle2 remains true
                // Do nothing to triangle3 (already false)
                newDiamonds[index].triangle4 = false;
              }
            } else if (index === segments.length - 1) {
              // Last segment: only modify triangle1 and triangle3 based on edge position
              if (closestEdgeResult.isOnLeftSide) {
                newDiamonds[index].triangle1 = false;
                // Do nothing to triangle2 (already false)
                // newDiamonds[index].triangle3 remains true
                // Do nothing to triangle4 (already false)
              } else {
                // newDiamonds[index].triangle1 remains true
                // Do nothing to triangle2 (already false)
                newDiamonds[index].triangle3 = false;
                // Do nothing to triangle4 (already false)
              }
            } else {
              // Middle segments: modify all triangles based on edge position
              if (closestEdgeResult.isOnLeftSide) {
                newDiamonds[index].triangle1 = false;
                newDiamonds[index].triangle2 = false;
              } else {
                newDiamonds[index].triangle3 = false;
                newDiamonds[index].triangle4 = false;
              }
            }
          }
        } else {
          // Create a parallel offset segment for visualization
          const dx = segment[1][0] - segment[0][0];
          const dy = segment[1][1] - segment[0][1];
          const len = Math.sqrt(dx * dx + dy * dy);
          if (len > 0) {
            const offsetX = (-dy * 20) / len; // perpendicular offset
            const offsetY = (dx * 20) / len;
            newClosestEdges.push([
              [segment[0][0] + offsetX, segment[0][1] + offsetY],
              [segment[1][0] + offsetX, segment[1][1] + offsetY],
            ] as LineSegment2);
          } else {
            newClosestEdges.push(segment);
          }
        }
      });

      setPreviewClosestEdges(newClosestEdges);

      // Update the wedge visualization with the adjusted diamonds
      if (wedgeVisualization && hasChanges) {
        // Force immediate update of visualization
        prevVisualizationRef.current = {
          ...wedgeVisualization,
          diamonds: newDiamonds,
          mousePosition: wedgeVisualization.mousePosition,
        };

        // Mark that we've processed edge detection for this visualization
        edgeDetectionProcessedRef.current = true;

        // Use debounced update to reduce flickering
        updateDiamondsWithDebounce(newDiamonds);
      }
    } else {
      setPreviewClosestEdges([]);
    }
  }, [
    wedgeVisualization,
    renderedSheetShapes,
    unitScale,
    updateDiamondsWithDebounce,
  ]);

  // Effect for finding closest edges for placed segments
  useEffect(() => {
    if (points.length > 1 && renderedSheetShapes && unitScale) {
      const segments: LineSegment2[] = [];

      // Create segments from placed points
      for (let i = 0; i < points.length - 1; i++) {
        segments.push([points[i], points[i + 1]]);
      }

      // Find closest edge for each segment
      const newClosestEdges: LineSegment2[] = [];
      // Create copy of existing diamonds with special rules for first and last segments
      const newDiamonds: Diamond[] = segments.map((_, segmentIndex) => {
        if (segmentIndex === 0) {
          // First segment should never have triangle1 or triangle3 visible
          return {
            triangle1: false,
            triangle2: true,
            triangle3: false,
            triangle4: true,
          };
        } else if (segmentIndex === segments.length - 1) {
          // Last segment should never have triangle2 or triangle4 visible
          return {
            triangle1: true,
            triangle2: false,
            triangle3: true,
            triangle4: false,
          };
        } else {
          // Middle segments start with all triangles visible
          return {
            triangle1: true,
            triangle2: true,
            triangle3: true,
            triangle4: true,
          };
        }
      });

      segments.forEach((segment, index) => {
        const closestEdgeResult = findClosestParallelEdgeToSegment(
          segment,
          renderedSheetShapes,
          0.1,
          unitScale
        );

        if (closestEdgeResult) {
          newClosestEdges.push(closestEdgeResult.edge);

          // Check if edge is closer than 1m and adjust diamonds accordingly
          if (
            closestEdgeResult.isCloserThan100mm &&
            index < newDiamonds.length
          ) {
            // Skip the special triangles for first and last segments
            if (index === 0) {
              // First segment: only modify triangle2 and triangle4 based on edge position
              if (closestEdgeResult.isOnLeftSide) {
                // Do nothing to triangle1 (already false)
                newDiamonds[index].triangle2 = false;
                // Do nothing to triangle3 (already false)
                // newDiamonds[index].triangle4 remains true
              } else {
                // Do nothing to triangle1 (already false)
                // newDiamonds[index].triangle2 remains true
                // Do nothing to triangle3 (already false)
                newDiamonds[index].triangle4 = false;
              }
            } else if (index === segments.length - 1) {
              // Last segment: only modify triangle1 and triangle3 based on edge position
              if (closestEdgeResult.isOnLeftSide) {
                newDiamonds[index].triangle1 = false;
                // Do nothing to triangle2 (already false)
                // newDiamonds[index].triangle3 remains true
                // Do nothing to triangle4 (already false)
              } else {
                // newDiamonds[index].triangle1 remains true
                // Do nothing to triangle2 (already false)
                newDiamonds[index].triangle3 = false;
                // Do nothing to triangle4 (already false)
              }
            } else {
              // Middle segments: modify all triangles based on edge position
              if (closestEdgeResult.isOnLeftSide) {
                newDiamonds[index].triangle1 = false;
                newDiamonds[index].triangle2 = false;
              } else {
                newDiamonds[index].triangle3 = false;
                newDiamonds[index].triangle4 = false;
              }
            }
          }
        } else {
          // Create a parallel offset segment for visualization
          const dx = segment[1][0] - segment[0][0];
          const dy = segment[1][1] - segment[0][1];
          const len = Math.sqrt(dx * dx + dy * dy);
          if (len > 0) {
            const offsetX = (-dy * 20) / len; // perpendicular offset
            const offsetY = (dx * 20) / len;
            newClosestEdges.push([
              [segment[0][0] + offsetX, segment[0][1] + offsetY],
              [segment[1][0] + offsetX, segment[1][1] + offsetY],
            ] as LineSegment2);
          } else {
            newClosestEdges.push(segment);
          }
        }
      });

      setClosestEdges(newClosestEdges);
      setDiamonds(newDiamonds);
    } else {
      setClosestEdges([]);
    }
  }, [points, renderedSheetShapes, unitScale]);

  // Helper function to handle keydown events for changing the default wedge type
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Only handle keyboard events when we're in the preview phase (after first click, before second)
      if (points.length !== 1 || !intermediatePoint) {
        return;
      }

      // Find the current index in the available wedge types
      const currentIndex = availableWedgeTypes.findIndex(
        (item) => item.type === defaultMiddleWedgeType
      );

      if (currentIndex === -1) return;

      let newIndex = currentIndex;

      // Up arrow - go to longer wedge
      if (event.key === 'ArrowUp') {
        newIndex = Math.min(currentIndex + 1, availableWedgeTypes.length - 1);
      }
      // Down arrow - go to shorter wedge
      else if (event.key === 'ArrowDown') {
        newIndex = Math.max(currentIndex - 1, 0);
      } else {
        return; // Not an arrow key we care about
      }

      // If the index changed, update the default wedge type
      if (newIndex !== currentIndex) {
        setDefaultMiddleWedgeType(availableWedgeTypes[newIndex].type);

        // Clear the cached visualization to force a recalculation
        prevVisualizationRef.current = null;
      }
    },
    [
      points.length,
      intermediatePoint,
      defaultMiddleWedgeType,
      availableWedgeTypes,
    ]
  );

  // Add event listener for keydown events
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const edges = useMemo(() => {
    if (points.length <= 1) return [];

    const edges: LineSegment2[] = [];
    for (let i = 0; i < points.length - 1; i++) {
      edges.push([points[i], points[i + 1]]);
    }
    return edges;
  }, [points]);

  const emitResult = useCallback(
    (
      points: Point2[],
      diamonds: Diamond[],
      kileType: KileTypeSKU[],
      totalWedgeProductLength: number
    ) => {
      if (points.length > 1) {
        onResult({
          points,
          valid: true,
          diamond: diamonds,
          kileType: kileType,
          totalWedgeProductLength: totalWedgeProductLength,
        });
      } else {
        onResult({
          points,
          valid: false,
          diamond: diamonds,
          kileType: kileType,
          totalWedgeProductLength: totalWedgeProductLength,
        });
      }
    },
    [onResult]
  );

  const getPoint = useCallback(
    (event: MouseEvent): Point2 => {
      const domPoint: Point2 = [event.clientX, event.clientY];

      if (!isShiftPressed) {
        return getPointInPdfCoordinateSystem(domPoint);
      }

      // If first point is placed, snap to horizontal/vertical
      if (points.length > 0) {
        const firstPoint = points[0];
        const domFirstPoint = getPointInDomCoordinateSystem(firstPoint);

        const deltaX = domPoint[0] - domFirstPoint[0];
        const deltaY = domPoint[1] - domFirstPoint[1] - HEADER_HEIGHT;

        if (Math.abs(deltaX) > Math.abs(deltaY)) {
          return getPointInPdfCoordinateSystem([
            domPoint[0],
            domFirstPoint[1] + HEADER_HEIGHT,
          ]);
        } else {
          return getPointInPdfCoordinateSystem([domFirstPoint[0], domPoint[1]]);
        }
      }

      return getPointInPdfCoordinateSystem(domPoint);
    },
    [
      getPointInPdfCoordinateSystem,
      isShiftPressed,
      points,
      getPointInDomCoordinateSystem,
    ]
  );

  const handleViewerClick = useCallback(
    (event: MouseEvent) => {
      if (event.button !== 0 || !unitScale) {
        return;
      }

      const newPoint = getPoint(event);

      // If this is the first point, just add it
      if (points.length === 0) {
        setPoints([newPoint]);
        emitResult([newPoint], diamonds, kileTypes, 0);
        return;
      }

      // If this is the second point, calculate all wedge points
      if (points.length === 1) {
        const startPoint = points[0];

        // Use the preview visualization diamonds if available since they already have
        // the correct triangle adjustments from edge detection
        const result = prevVisualizationRef.current
          ? {
              ...prevVisualizationRef.current,
              points: [...prevVisualizationRef.current.points],
            }
          : calculateWedgePoints(
              startPoint,
              newPoint,
              unitScale,
              diamonds[0],
              defaultMiddleWedgeType
            );

        // Set the new points, diamonds, and kile types
        setPoints(result.points);
        setDiamonds(result.diamonds);
        setKileTypes(result.kileTypes);

        // Instead of emitting the result right away, wait for the closest edges effect to run
        // and adjust the diamonds based on edge proximity
        // We'll use a setTimeout to ensure the edge detection effect has time to run
        setTimeout(() => {
          // Check all segments for nearby edges, especially the last segment
          if (renderedSheetShapes) {
            const segments: LineSegment2[] = [];
            for (let i = 0; i < result.points.length - 1; i++) {
              segments.push([result.points[i], result.points[i + 1]]);
            }

            // Create a copy of the diamonds with special rules for first and last segments
            const finalDiamonds: Diamond[] = segments.map((_, segmentIndex) => {
              if (segmentIndex === 0) {
                // First segment should never have triangle1 or triangle3 visible
                return {
                  triangle1: false,
                  triangle2: true,
                  triangle3: false,
                  triangle4: true,
                };
              } else if (segmentIndex === segments.length - 1) {
                // Last segment should never have triangle2 or triangle4 visible
                return {
                  triangle1: true,
                  triangle2: false,
                  triangle3: true,
                  triangle4: false,
                };
              } else {
                // Middle segments start with all triangles visible
                return {
                  triangle1: true,
                  triangle2: true,
                  triangle3: true,
                  triangle4: true,
                };
              }
            });

            let hasChanges = false;

            segments.forEach((segment, index) => {
              const closestEdgeResult = findClosestParallelEdgeToSegment(
                segment,
                renderedSheetShapes,
                0.1,
                unitScale
              );

              if (
                closestEdgeResult &&
                closestEdgeResult.isCloserThan100mm &&
                index < finalDiamonds.length
              ) {
                hasChanges = true;

                // Skip the special triangles for first and last segments
                if (index === 0) {
                  // First segment: only modify triangle2 and triangle4 based on edge position
                  if (closestEdgeResult.isOnLeftSide) {
                    // Do nothing to triangle1 (already false)
                    finalDiamonds[index].triangle2 = false;
                    // Do nothing to triangle3 (already false)
                    // finalDiamonds[index].triangle4 remains true
                  } else {
                    // Do nothing to triangle1 (already false)
                    // finalDiamonds[index].triangle2 remains true
                    // Do nothing to triangle3 (already false)
                    finalDiamonds[index].triangle4 = false;
                  }
                } else if (index === segments.length - 1) {
                  // Last segment: only modify triangle1 and triangle3 based on edge position
                  if (closestEdgeResult.isOnLeftSide) {
                    finalDiamonds[index].triangle1 = false;
                    // Do nothing to triangle2 (already false)
                    // finalDiamonds[index].triangle3 remains true
                    // Do nothing to triangle4 (already false)
                  } else {
                    // finalDiamonds[index].triangle1 remains true
                    // Do nothing to triangle2 (already false)
                    finalDiamonds[index].triangle3 = false;
                    // Do nothing to triangle4 (already false)
                  }
                } else {
                  // Middle segments: modify all triangles based on edge position
                  if (closestEdgeResult.isOnLeftSide) {
                    finalDiamonds[index].triangle1 = false;
                    finalDiamonds[index].triangle2 = false;
                  } else {
                    finalDiamonds[index].triangle3 = false;
                    finalDiamonds[index].triangle4 = false;
                  }
                }
              }
            });

            // Only update if we found edges that require triangle adjustments
            if (hasChanges) {
              setDiamonds(finalDiamonds);
            } else {
              // Even if no edges are close, still apply the special rules for first/last segments
              setDiamonds(finalDiamonds);
            }
          }

          // Now emit the final result with possibly adjusted diamonds
          emitResult(
            result.points,
            result.diamonds,
            result.kileTypes,
            result.segmentLengths?.reduce((sum, length) => sum + length, 0) ?? 0
          );
        }, 0);
      }
    },
    [
      diamonds,
      emitResult,
      getPoint,
      kileTypes,
      points,
      unitScale,
      defaultMiddleWedgeType,
      renderedSheetShapes,
    ]
  );

  const { viewerRef } = useSheetViewer();
  useEffect(() => {
    const current = viewerRef.current;
    if (!current) {
      return;
    }
    current.addEventListener('click', handleViewerClick);
    return () => {
      current.removeEventListener('click', handleViewerClick);
    };
  }, [handleViewerClick, viewerRef]);

  // Debug flag to visualize coordinate transformations - set to false to disable debug features
  const showDebugInfo = false;

  // The debugging code is temporarily disabled to avoid linter errors.
  // Enable manually when needed for debugging.

  // Create a pointer move handler
  const handlePointerMove = useCallback(
    (event: MouseEvent) => {
      // Only show intermediate points if we have already placed the first point
      if (points.length === 0) {
        const newPoint = getPoint(event);
        setIntermediatePoint(newPoint);
        return;
      }

      // If already have more than one point, don't show intermediate points
      if (points.length > 1) {
        setIntermediatePoint(null);
        return;
      }

      const newPoint = getPoint(event);

      // Only reset edge detection on significant movement to reduce flickering
      if (
        intermediatePoint &&
        (Math.abs(intermediatePoint[0] - newPoint[0]) > 5 ||
          Math.abs(intermediatePoint[1] - newPoint[1]) > 5)
      ) {
        // Reset edge detection flag to force rechecking
        edgeDetectionProcessedRef.current = false;
      }

      setIntermediatePoint(newPoint);
    },
    [getPoint, points.length, intermediatePoint]
  );

  useEffect(() => {
    const current = viewerRef.current;
    if (!current) {
      return;
    }
    current.addEventListener('pointermove', handlePointerMove);
    return () => {
      current.removeEventListener('pointermove', handlePointerMove);
    };
  }, [handlePointerMove, viewerRef]);

  // Track which point is being dragged - start or end
  const [dragState, setDragState] = useState<DragStateType>('none');
  const dragIndexRef = useRef<number | null>(null);

  // Helper to update drag state after drag operation
  const endDragOperation = useCallback(() => {
    setDragState('none');
    dragIndexRef.current = null;
  }, []);

  // Helper function to handle the drag start
  const handleDragStart = useCallback(
    (index: number) => {
      // Always set the dragIndexRef first
      dragIndexRef.current = index;

      if (index === 0) {
        setDragState('start');
      } else if (index === points.length - 1) {
        setDragState('end');
      } else {
        setDragState('middle');
      }
    },
    [points.length]
  );

  // Helper function to handle simple drag case
  const handleSimpleDrag = useCallback(
    (index: number, newPoint: Point2, isPointerUp: boolean) => {
      const newPoints = [...points];
      newPoints[index] = newPoint;
      setPoints(newPoints);

      if (isPointerUp) {
        emitResult(newPoints, diamonds, kileTypes, 0);
        endDragOperation();
      }
    },
    [points, diamonds, kileTypes, emitResult, endDragOperation]
  );

  // Simplified vertex drag handler that delegates to the separate utility function
  const handleVertexDrag = useCallback(
    (event: MouseEvent, index: number) => {
      setIntermediatePoint(null);

      // Use the same getPoint function that accounts for HEADER_HEIGHT
      const newPoint = getPoint(event);
      const isPointerUp = event.type === 'pointerup';
      const isPointerMove = event.type === 'pointermove';

      // When drag starts, remember what kind of point we're dragging
      if (event.type === 'pointerdown') {
        handleDragStart(index);
        // Return early on pointerdown, we just want to set up the drag state
        return;
      }

      // If we're not in the middle of dragging, don't process move events
      if (dragState === 'none' && !isPointerUp) {
        return;
      }

      // Handle simple case first - single point or no unit scale
      if (points.length <= 1 || !unitScale) {
        handleSimpleDrag(index, newPoint, isPointerUp);
        return;
      }

      // Process the drag based on its type
      processDragByType(
        dragState,
        newPoint,
        isPointerUp,
        dragIndexRef,
        points,
        diamonds,
        kileTypes,
        unitScale,
        recalculateWedgePoints,
        setPoints,
        emitResult,
        endDragOperation
      );
    },
    [
      getPoint,
      points,
      unitScale,
      dragState,
      handleDragStart,
      handleSimpleDrag,
      recalculateWedgePoints,
      diamonds,
      kileTypes,
      dragIndexRef,
      emitResult,
      endDragOperation,
      setPoints,
    ]
  );

  // Clean up debounce timer on unmount
  useEffect(() => {
    // No setup logic needed

    // Return cleanup function
    return () => {
      if (debounceTimerRef.current !== null) {
        window.clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // getAdjustedDomCoordinates as per your reverted state (using prop + HEADER_HEIGHT)
  const getAdjustedDomCoordinates = useCallback(
    (point: Point2): Point2 => {
      const domPoint = getPointInDomCoordinateSystem(point);
      return [domPoint[0], domPoint[1]];
    },
    [getPointInDomCoordinateSystem]
  );

  const updateWedgeCalculations = useCallback(
    // eslint-disable-next-line complexity
    (currentPoints: Point2[]) => {
      const numSegments = currentPoints.length - 1;
      if (numSegments < 0) {
        setDiamonds([]);
        setKileTypes([]);
        return;
      }
      if (numSegments === 0) {
        setDiamonds([
          {
            triangle1: true,
            triangle2: true,
            triangle3: true,
            triangle4: true,
          },
        ]);
        setKileTypes([KileTypeSKU.RW257379]);
        return;
      }
      if (!unitScale || !renderedSheetShapes) {
        const fallbackDiamonds = Array(numSegments)
          .fill(null)
          .map((_, i) => {
            let diamond = {
              triangle1: true,
              triangle2: true,
              triangle3: true,
              triangle4: true,
            };
            if (i === 0) {
              diamond.triangle1 = false;
              diamond.triangle3 = false;
            }
            if (i === numSegments - 1) {
              diamond.triangle2 = false;
              diamond.triangle4 = false;
            }
            return diamond;
          });
        setDiamonds(fallbackDiamonds);
        setKileTypes(Array(numSegments).fill(KileTypeSKU.RW257379));
        return;
      }
      const finalDiamonds: Diamond[] = [];
      const finalKileTypes: KileTypeSKU[] = [];
      for (let i = 0; i < numSegments; i++) {
        const segment: LineSegment2 = [currentPoints[i], currentPoints[i + 1]];
        const segmentLengthPdf = Math.sqrt(
          Math.pow(segment[1][0] - segment[0][0], 2) +
            Math.pow(segment[1][1] - segment[0][1], 2)
        );
        const segmentLengthScaled = segmentLengthPdf * unitScale;
        let diamond: Diamond = {
          triangle1: true,
          triangle2: true,
          triangle3: true,
          triangle4: true,
        };
        if (i === 0) {
          diamond.triangle1 = false;
          diamond.triangle3 = false;
        }
        if (i === numSegments - 1) {
          diamond.triangle2 = false;
          diamond.triangle4 = false;
        }
        const closestEdgeResult = findClosestParallelEdgeToSegment(
          segment,
          renderedSheetShapes,
          0.1,
          unitScale
        );
        if (closestEdgeResult && closestEdgeResult.isCloserThan100mm) {
          if (i === 0) {
            if (closestEdgeResult.isOnLeftSide) diamond.triangle2 = false;
            else diamond.triangle4 = false;
          } else if (i === numSegments - 1) {
            if (closestEdgeResult.isOnLeftSide) diamond.triangle1 = false;
            else diamond.triangle3 = false;
          } else {
            if (closestEdgeResult.isOnLeftSide) {
              diamond.triangle1 = false;
              diamond.triangle2 = false;
            } else {
              diamond.triangle3 = false;
              diamond.triangle4 = false;
            }
          }
        }
        finalDiamonds.push(diamond);
        const kileType = selectWedgeType(diamond, segmentLengthScaled);
        finalKileTypes.push(kileType);
      }
      setDiamonds(finalDiamonds);
      setKileTypes(finalKileTypes);
    },
    [unitScale, renderedSheetShapes, setDiamonds, setKileTypes]
  );

  // <<< NEW USE EFFECT HOOKS >>>

  // useEffect for Initialization
  useEffect(() => {
    const initialPoints = initialState?.points ?? [];
    setPoints(initialPoints); // Update local points state
    updateWedgeCalculations(initialPoints); // Call the unified calculation function
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialState, updateWedgeCalculations]);

  // useEffect for Updates (points change, or context like scale/shapes becomes available/changes)
  useEffect(() => {
    // If points change (e.g., after drag) or if context needed for calculation changes, recalculate.
    updateWedgeCalculations(points);
  }, [points, unitScale, renderedSheetShapes, updateWedgeCalculations]);

  // <<< REMOVE OLD EDGE DETECTION EFFECT >>>
  // Find and remove the useEffect whose dependencies are primarily [points, renderedSheetShapes, unitScale]
  /* e.g., might look like:
  useEffect(() => {
    if (points.length > 1 && renderedSheetShapes && unitScale) {
      // ... old edge detection logic calling setDiamonds ... 
    }
  }, [points, renderedSheetShapes, unitScale]);
  */

  // Keep the useEffect for disabling text selection during drag
  useEffect(() => {
    const originalUserSelect = document.body.style.userSelect;
    if (dragState !== 'none') {
      document.body.style.userSelect = 'none';
    } else {
      document.body.style.userSelect = originalUserSelect;
    }
    return () => {
      document.body.style.userSelect = originalUserSelect;
    };
  }, [dragState]);

  return (
    <>
      {/* Vertices are rendered here, outside the new SVG overlay */}
      {/* This assumes SheetVertex renders a <Box> for absolute positioning */}

      {/* Render vertices for placed points */}
      {points.map((point, index) => (
        <DraggableVertex
          key={index}
          theme={index === 0 ? 'inverted' : 'default'}
          point={point}
          onVertexDrag={(event) => handleVertexDrag(event, index)}
          getPointInDomCoordinateSystem={getAdjustedDomCoordinates}
        />
      ))}

      {/* Render intermediate vertices when hovering */}
      {wedgeVisualization &&
        points.length === 1 &&
        wedgeVisualization.points
          .slice(1)
          .map((point, index) => (
            <DraggableVertex
              key={`preview-vertex-${index}`}
              theme="default"
              point={point}
              opacity={0.5}
              getPointInDomCoordinateSystem={getAdjustedDomCoordinates}
              style={{ pointerEvents: 'none' }}
            />
          ))}

      {/* Dedicated SVG overlay for LABELS ONLY */}
      <chakra.svg
        position="absolute"
        top="0"
        left="0"
        width="100%"
        height="100%"
        pointerEvents="none" // So it doesn't interfere with vertex dragging if vertices are underneath
        zIndex="overlay" // Or an appropriate z-index
      >
        {/* Render visualization of WEDGE AND DIAMOND LABELS during hover */}
        {wedgeVisualization && points.length === 1 && (
          <>
            {/* Preview wedge diamond labels */}
            {isProtan &&
              wedgeVisualization.points.slice(0, -1).map((point, index) => (
                <DiamondLabel
                  key={`preview-diamond-${index}`}
                  diamond={wedgeVisualization.diamonds[index]}
                  segment={[
                    [point[0], point[1]],
                    [
                      wedgeVisualization.points[index + 1][0],
                      wedgeVisualization.points[index + 1][1],
                    ],
                  ]}
                  getPointInDomCoordinateSystem={getAdjustedDomCoordinates}
                />
              ))}

            {/* Preview wedge labels */}
            {isProtan &&
              wedgeVisualization.points.slice(0, -1).map((point, index) => {
                const segment = [
                  [point[0], point[1]],
                  [
                    wedgeVisualization.points[index + 1][0],
                    wedgeVisualization.points[index + 1][1],
                  ],
                ] as [Point2, Point2];
                const midpoint = [
                  (segment[0][0] + segment[1][0]) / 2,
                  (segment[0][1] + segment[1][1]) / 2,
                ] as Point2;
                const tangentVector = [
                  segment[1][0] - segment[0][0],
                  segment[1][1] - segment[0][1],
                ] as Point2;
                const kileType =
                  wedgeVisualization.kileTypes?.[index] ||
                  ('K2U2' as KileTypeSKU);
                return (
                  <WedgePreviewLabel
                    key={`preview-wedge-label-${index}`}
                    position={midpoint}
                    kileType={kileType}
                    diamond={wedgeVisualization.diamonds[index]}
                    getPointInDomCoordinateSystem={getAdjustedDomCoordinates}
                    unitScale={unitScale}
                    tangentVector={tangentVector}
                  />
                );
              })}
          </>
        )}

        {/* Render diamond LABELS for existing edges */}
        {isProtan &&
          edges.length > 0 &&
          edges.map((edge, index) => (
            <DiamondLabel
              key={`diamond-${index}`}
              diamond={diamonds[index]}
              segment={[
                [edge[0][0], edge[0][1]],
                [edge[1][0], edge[1][1]],
              ]}
              getPointInDomCoordinateSystem={getAdjustedDomCoordinates}
            />
          ))}

        {/* Render wedge LABELS for existing edges */}
        {isProtan &&
          edges.length > 0 &&
          edges.map((edge, index) => {
            const midpoint = [
              (edge[0][0] + edge[1][0]) / 2,
              (edge[0][1] + edge[1][1]) / 2,
            ] as Point2;
            const tangentVector = [
              edge[1][0] - edge[0][0],
              edge[1][1] - edge[0][1],
            ] as Point2;
            return (
              <WedgePreviewLabel
                key={`wedge-label-${index}`}
                position={midpoint}
                kileType={kileTypes[index] || ('K2U2' as KileTypeSKU)}
                diamond={diamonds[index]}
                getPointInDomCoordinateSystem={getAdjustedDomCoordinates}
                unitScale={unitScale}
                tangentVector={tangentVector}
              />
            );
          })}
      </chakra.svg>
    </>
  );
};
